/*******************************************************************************
 * Copyright (c) 2019. BMW Group. All rights reserved.
 ******************************************************************************/
import {
  Component, ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {StatefulComponent} from '../../../stateful/stateful.component';
import {InputCriteriaAutocompleteMultiSelectionState} from './models/input-criteria-autocomplete-multi-selection.state';
import {GlobalSearchCriteriaModel} from '../../models/global-search-criteria.model';
import {GlobalSearchCriteriaService} from '../../services/global-search-criteria.service';
import {ReferenceDataModel} from '../../../../api/reference-data.model';
import {
    Observable,
    of,
    Subject,
    Subscription
} from 'rxjs';
import {InputCriteriaTypeMapper} from '../mappers/input-criteria-type.mapper';
import {
    debounceTime,
    distinctUntilChanged,
    switchMap
} from 'rxjs/operators';
import {inputCriteriaAutocompleteMultiSelectionConfiguration} from './configuration/input-criteria-autocomplete-multi-selection.configuration';
import {inputCriteriaTypeConfiguration} from '../configuration/input-criteria-type-configuration';

@Component({
    selector: 'app-input-criteria-autocomplete-multi-selection',
    templateUrl: './input-criteria-autocomplete-multi-selection.component.html',
    styleUrls: ['./input-criteria-autocomplete-multi-selection.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})

export class InputCriteriaAutocompleteMultiSelectionComponent
        extends StatefulComponent<InputCriteriaAutocompleteMultiSelectionState>
        implements OnInit, OnDestroy {
    @Input() globalSearchCriteriaModel: GlobalSearchCriteriaModel;
    @Output() searchCriteriaAutocompleteMultiSelectionReset = new EventEmitter<void>();
    @ViewChild('inputSearch', {static: true}) inputSearch: ElementRef;

    closeCode = new EventEmitter<string>();
    dropdownOpenMenu = new EventEmitter<boolean>();
    private subscriptions = new Subscription();
    private readonly searchSubject = new Subject<string>();

    constructor(private globalSearchService: GlobalSearchCriteriaService) {
        super();
    }

    get inputPlaceholder(): string {
        const criterionFilterName = this.globalSearchService.getSelectedSearchCriterionName();
        if (criterionFilterName != null) {
            return `${inputCriteriaTypeConfiguration?.inputAutoCompleteMultiSelectionPlaceholder} ${criterionFilterName.toLowerCase()}`;
        }
    }

    ngOnInit(): void {
        const resolverSubscriber = this.globalSearchCriteriaModel?.autoCompleteDataResolverByCriterionCode
                .get(this.globalSearchService.getSelectedSearchCriterionFilter()?.criterion);
        this.subscriptions.add(
                this.searchSubject
                        .pipe(
                                debounceTime(inputCriteriaAutocompleteMultiSelectionConfiguration.autocompleteDebounceTime),
                                distinctUntilChanged(),
                                switchMap((searchTerm: string) => {
                                    let resultsObservable: Observable<ReferenceDataModel[]> = of([]);
                                    if (searchTerm.length > 0) {
                                        resultsObservable = resolverSubscriber(
                                                searchTerm,
                                                inputCriteriaAutocompleteMultiSelectionConfiguration.paginationConfiguration.firstResult,
                                                inputCriteriaAutocompleteMultiSelectionConfiguration.paginationConfiguration.maxResults);
                                    }
                                    return resultsObservable;
                                })
                        )
                        .subscribe((dropdownOptions: ReferenceDataModel[]) => {
                            this.dropdownOpenMenu.emit(dropdownOptions.length > 0);
                            this.storeState({dropdownOptions: dropdownOptions});
                        }));

        this.storeState({
            dropdownOpen: false,
            selectedOptions: []
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    onSearchQueryInput(input: HTMLInputElement): void {
        this.searchSubject.next(input?.value);
    }

    removeOption(code: string): void {
        const indexToRemove = this.state.selectedOptions.findIndex((obj) => obj.code === code);
        if (indexToRemove > -1) {
            this.state.selectedOptions.splice(indexToRemove, 1);
            this.closeCode.emit(code);
        }
    }

    addOption(option: ReferenceDataModel): void {
        this.inputSearch.nativeElement.focus();
        this.state.selectedOptions.push(option);
    }

    dropdownAutocompleteLabel(): string {
        return this.globalSearchService.getSelectedSearchCriterionName()
                ?.toLowerCase();
    }

    addChipsQuery(): void {
        const queryFilter = InputCriteriaTypeMapper.fromInputCriteriaTypeToQueryChipsAreaModel(
                this.globalSearchService.getSelectedSearchCriterionFilter(),
                this.state.selectedOptions.map((option) =>  `${option.code} - ${option.name}`)
                        .join(' | '),
                this.state.selectedOptions.map((option) => option.code));

        if (queryFilter) {
            this.globalSearchService.addCriterionFilterToQuery(queryFilter);
            this.dropdownOpenMenu.emit(false);
            this.searchCriteriaAutocompleteMultiSelectionReset.emit();
            this.storeState({selectedOptions: null});
        }
    }
}
